// src/components/ExternalStyleViewer.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Viewer from './viewer'; // Reusing your existing 3D viewer logic
import toast from 'react-hot-toast';

const NAVY = '#001F3F';
const GOLD = '#B8860B';

export default function ExternalStyleViewer() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [pinInput, setPinInput] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [loading, setLoading] = useState(false);

  const verifyPin = async () => {
    if (pinInput.length !== 6) return toast.error("Enter 6-digit PIN");
    setLoading(true);
    try {
      const docRef = doc(db, 'share_codes', code);
      const snap = await getDoc(docRef);

      if (snap.exists()) {
        const data = snap.data();
        if (data.code === pinInput) {
          // Check if expired (2-hour window)
          const now = new Date();
          const expiry = data.expiresAt.toDate();
          if (now > expiry) {
            toast.error("Access link expired. Ask client for a new one.");
          } else {
            setShareData(data);
            setIsUnlocked(true);
            toast.success("Identity Unlocked");
          }
        } else {
          toast.error("Incorrect PIN");
        }
      } else {
        toast.error("Invalid access link");
      }
    } catch (err) {
      toast.error("Connection error");
    } finally {
      setLoading(false);
    }
  };

  // If not unlocked, show the PIN Gate
  if (!isUnlocked) {
    return (
      <div style={gatePage}>
        <div style={pinCard}>
          <div style={brandHeader}>ESDRAS 3D</div>
          <h2 style={title}>Client Vault</h2>
          <p style={subtitle}>Enter the PIN sent by your client to view their 3D head scan and requested style.</p>
          
          <input 
            type="text" 
            placeholder="******" 
            maxLength={6}
            value={pinInput}
            onChange={(e) => setPinInput(e.target.value)}
            style={pinInputStyle}
          />
          
          <button onClick={verifyPin} disabled={loading} style={unlockBtn}>
            {loading ? 'OPENING...' : 'VIEW 3D MODEL'}
          </button>
        </div>
      </div>
    );
  }

  // Once unlocked, show the 3D Model and the CTA
  return (
    <div style={viewerContainer}>
      {/* Top Info Bar */}
      <div style={infoBar}>
        <div>
          <span style={{ color: GOLD, fontWeight: 'bold' }}>CLIENT:</span> {shareData?.userName}
        </div>
        <button onClick={() => navigate('/login')} style={topJoinBtn}>CLAIM PROFILE</button>
      </div>

      {/* The 3D Render Area */}
      <div style={renderWindow}>
        <Viewer 
          externalModelUrl={shareData?.lookbook[0]?.modelUrl} 
          isExternalView={true} 
        />
      </div>

      {/* Bottom Conversion Bar */}
      <div style={ctaBar}>
        <div style={ctaText}>
          <p style={{ margin: 0, fontWeight: 'bold', fontSize: '0.9rem' }}>Want 3D Precision for all your clients?</p>
          <p style={{ margin: 0, fontSize: '0.7rem', opacity: 0.7 }}>Get listed on the Esdras Map today.</p>
        </div>
        <button onClick={() => navigate('/login')} style={finalCtaBtn}>JOIN ESDRAS</button>
      </div>
    </div>
  );
}

// --- STYLES ---

const gatePage = {
  minHeight: '100vh',
  background: '#000',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  fontFamily: 'Montserrat, sans-serif'
};

const pinCard = {
  background: NAVY,
  padding: '40px 30px',
  borderRadius: '24px',
  border: `1px solid ${GOLD}`,
  width: '100%',
  maxWidth: '400px',
  textAlign: 'center',
  boxShadow: '0 20px 50px rgba(0,0,0,0.5)'
};

const brandHeader = { color: GOLD, letterSpacing: '4px', fontSize: '0.8rem', fontWeight: '900', marginBottom: '20px' };
const title = { color: 'white', margin: '0 0 10px 0', fontSize: '1.8rem' };
const subtitle = { color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', lineHeight: '1.5', marginBottom: '30px' };

const pinInputStyle = {
  width: '100%',
  background: 'rgba(0,0,0,0.3)',
  border: `1px solid rgba(184, 134, 11, 0.3)`,
  padding: '15px',
  borderRadius: '12px',
  color: GOLD,
  fontSize: '2rem',
  textAlign: 'center',
  letterSpacing: '10px',
  outline: 'none',
  marginBottom: '20px',
  boxSizing: 'border-box'
};

const unlockBtn = {
  width: '100%',
  background: GOLD,
  color: 'black',
  border: 'none',
  padding: '15px',
  borderRadius: '12px',
  fontWeight: '900',
  fontSize: '1rem',
  cursor: 'pointer',
  textTransform: 'uppercase'
};

const viewerContainer = {
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  background: '#000',
  color: 'white',
  fontFamily: 'Montserrat, sans-serif'
};

const infoBar = {
  padding: '15px 20px',
  background: NAVY,
  borderBottom: `2px solid ${GOLD}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontSize: '0.8rem'
};

const topJoinBtn = {
  background: 'transparent',
  border: `1px solid ${GOLD}`,
  color: GOLD,
  padding: '5px 12px',
  borderRadius: '4px',
  fontSize: '0.7rem',
  fontWeight: 'bold',
  cursor: 'pointer'
};

const renderWindow = {
  flex: 1,
  position: 'relative',
  background: '#050505'
};

const ctaBar = {
  padding: '20px',
  background: NAVY,
  borderTop: `1px solid rgba(184, 134, 11, 0.2)`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

const ctaText = { textAlign: 'left' };

const finalCtaBtn = {
  background: GOLD,
  color: 'black',
  border: 'none',
  padding: '12px 20px',
  borderRadius: '8px',
  fontWeight: '900',
  fontSize: '0.8rem',
  cursor: 'pointer'
};
            
